import React, { useEffect, useState } from 'react';
import { Globe, Code, Copy, Check, Save, Settings, Sparkles, RefreshCw, Image, LayoutGrid } from 'lucide-react';
import api from '../../services/api';
import alertService from '../../utils/alert';

import PageHeader from '../../components/PageHeader';
import { PAGE, PageLoader } from '../../components/crm/ui';
export default function LeadFormBuilder() {
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({
    title: 'Contact Us',
    description: 'Please fill out this form to get in touch.',
    fields: ['name', 'email', 'phoneNumber', 'notes'],
    customFields: [],
    themeColor: '#3b82f6',
    successMessage: 'Thank you! Your submission has been received.',
    logoUrl: '',
    footerText: '',
    pageCount: 1,
    columns: 1
  });

  useEffect(() => {
    async function loadConfig() {
      try {
        const res = await api.get('/crm/marketing/config');
        if (res.data.success && res.data.leadFormConfig) {
          setConfig({ customFields: [], logoUrl: '', footerText: '', pageCount: 1, columns: 1, ...res.data.leadFormConfig });
        }
      } catch (err) {
        console.error('Failed to load form config', err);
      } finally {
        setLoading(false);
      }
    }
    loadConfig();
  }, []);

  function slugifyFieldKey(text, existingKeys) {
    const base = text.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || 'field';
    let key = base;
    let i = 1;
    while (existingKeys.includes(key)) key = `${base}_${i++}`;
    return key;
  }

  const addCustomField = () => {
    setConfig(prev => {
      const customFields = prev.customFields || [];
      const label = `Custom Field ${customFields.length + 1}`;
      const key = slugifyFieldKey(label, customFields.map(f => f.key));
      return { ...prev, customFields: [...customFields, { key, label, type: 'text', required: false, options: [] }] };
    });
  };

  const updateCustomField = (index, patch) => {
    setConfig(prev => {
      const customFields = [...(prev.customFields || [])];
      customFields[index] = { ...customFields[index], ...patch };
      return { ...prev, customFields };
    });
  };

  const removeCustomField = (index) => {
    setConfig(prev => {
      const customFields = [...(prev.customFields || [])];
      customFields.splice(index, 1);
      return { ...prev, customFields };
    });
  };

  const formUrl = config.publicFormToken
    ? `${window.location.origin}/public-form.html?token=${config.publicFormToken}`
    : '';
  const iframeCode = `<iframe src="${formUrl}" width="100%" height="520" style="border:none; border-radius:12px; box-shadow:0 4px 20px rgba(0,0,0,0.08);"></iframe>`;

  const copyEmbedCode = () => {
    navigator.clipboard.writeText(iframeCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    alertService.success('Copied', 'Embed iframe code copied!');
  };

  const handleFieldToggle = (field) => {
    const fields = [...config.fields];
    if (fields.includes(field)) {
      // Must keep at least one field
      if (fields.length > 1) {
        setConfig({ ...config, fields: fields.filter(f => f !== field) });
      }
    } else {
      setConfig({ ...config, fields: [...fields, field] });
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const res = await api.put('/crm/marketing/config', config);
      if (res.data.success) {
        alertService.success('Success', 'Form settings saved successfully!');
      }
    } catch (err) {
      alertService.error('Error', err.response?.data?.message || 'Failed to save settings.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <PageLoader />
    );
  }

  return (
    <div className={PAGE}>
      <PageHeader
        title="Lead Form Builder"
        subtitle="Customize and generate embeddable lead capture webforms that flow directly into your CRM."
        icon={LayoutGrid}
      />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        {/* Left Side: Settings Panel */}
        <div className="lg:col-span-7 bg-white rounded-lg border border-[#E5E5E5] shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-6 space-y-6">
          <div className="flex items-center gap-2 border-b border-[#E5E5E5] pb-4">
            <Settings className="text-[#0176D3]" size={18} />
            <h2 className="text-[14px] font-bold text-[#2E2E2E]">Form Configuration</h2>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-[11px] font-semibold text-[#706E6B] mb-2">Form Title</label>
              <input
                type="text"
                value={config.title}
                onChange={e => setConfig({ ...config, title: e.target.value })}
                className="w-full h-9 px-3 bg-white border border-[#D8DDE6] rounded text-[13px] focus:outline-none focus:ring-1 focus:ring-[#0176D3] focus:border-[#0176D3]"
              />
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-[#706E6B] mb-2 flex items-center gap-1.5">
                <Image size={12} /> Logo URL
              </label>
              <input
                type="text"
                value={config.logoUrl}
                onChange={e => setConfig({ ...config, logoUrl: e.target.value })}
                placeholder="https://yourcdn.com/logo.png"
                className="w-full h-9 px-3 bg-white border border-[#D8DDE6] rounded text-[13px] focus:outline-none focus:ring-1 focus:ring-[#0176D3] focus:border-[#0176D3]"
              />
              <p className="text-[11px] text-[#706E6B] mt-1.5">Shown centered above the form title. Leave blank to hide.</p>
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-[#706E6B] mb-2">Description / Instructions</label>
              <textarea
                value={config.description}
                onChange={e => setConfig({ ...config, description: e.target.value })}
                className="w-full h-9 px-3 bg-white border border-[#D8DDE6] rounded text-[13px] focus:outline-none focus:ring-1 focus:ring-[#0176D3] focus:border-[#0176D3] h-20"
              />
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-[#706E6B] mb-2">Success/Thank-You Message</label>
              <input
                type="text"
                value={config.successMessage}
                onChange={e => setConfig({ ...config, successMessage: e.target.value })}
                className="w-full h-9 px-3 bg-white border border-[#D8DDE6] rounded text-[13px] focus:outline-none focus:ring-1 focus:ring-[#0176D3] focus:border-[#0176D3]"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-[11px] font-semibold text-[#706E6B] mb-2">Enabled Fields</label>
                <div className="space-y-2">
                  {['name', 'email', 'phoneNumber', 'notes'].map((field) => (
                    <label key={field} className="flex items-center gap-2.5 text-sm font-medium text-[#2E2E2E] cursor-pointer">
                      <input
                        type="checkbox"
                        checked={config.fields.includes(field)}
                        onChange={() => handleFieldToggle(field)}
                        className="rounded border-[#C9C9C9] text-[#0176D3] focus:ring-[#0176D3]"
                      />
                      {field === 'phoneNumber' ? 'Phone Number' : field.charAt(0).toUpperCase() + field.slice(1)}
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-[11px] font-semibold text-[#706E6B] mb-2">Brand Theme Color</label>
                <div className="flex gap-3 items-center">
                  <input
                    type="color"
                    value={config.themeColor}
                    onChange={e => setConfig({ ...config, themeColor: e.target.value })}
                    className="w-12 h-9 rounded border border-[#D8DDE6] cursor-pointer p-0 overflow-hidden"
                  />
                  <input
                    type="text"
                    value={config.themeColor}
                    onChange={e => setConfig({ ...config, themeColor: e.target.value })}
                    className="flex-1 px-4 py-2.5 bg-white border border-[#D8DDE6] rounded text-[13px] font-mono focus:outline-none"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-[#706E6B] mb-2">Form Layout</label>
              <div className="flex gap-2">
                {[1, 2, 4, 6].map(n => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setConfig({ ...config, pageCount: n })}
                    className={`flex-1 py-2 rounded-lg text-xs font-bold border transition ${
                      config.pageCount === n
                        ? 'bg-[#0176D3] text-white border-[#0176D3]'
                        : 'bg-white text-[#5C5C5C] border-[#E5E5E5] hover:border-[#0176D3]'
                    }`}
                  >
                    {n === 1 ? 'Single Page' : `${n} Pages`}
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-[#706E6B] mt-1.5">
                Fields are split evenly across pages, with Next/Back navigation and a progress bar. Choose more pages for longer intake forms to reduce drop-off.
              </p>
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-[#706E6B] mb-2 flex items-center gap-1.5">
                <LayoutGrid size={12} /> Field Columns
              </label>
              <div className="flex gap-2">
                {[1, 2, 3, 4].map(n => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setConfig({ ...config, columns: n })}
                    className={`flex-1 py-2 rounded-lg text-xs font-bold border transition ${
                      (config.columns || 1) === n
                        ? 'bg-[#0176D3] text-white border-[#0176D3]'
                        : 'bg-white text-[#5C5C5C] border-[#E5E5E5] hover:border-[#0176D3]'
                    }`}
                  >
                    {n === 1 ? 'Stacked' : `${n} Columns`}
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-[#706E6B] mt-1.5">
                Lays fields side-by-side within each page instead of stacking them full-width. Notes/checkbox fields always span the full row.
              </p>
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-[#706E6B] mb-2">Footer Text</label>
              <textarea
                value={config.footerText}
                onChange={e => setConfig({ ...config, footerText: e.target.value })}
                placeholder="e.g. company address, phone number, or a privacy disclaimer"
                className="w-full h-9 px-3 bg-white border border-[#D8DDE6] rounded text-[13px] focus:outline-none focus:ring-1 focus:ring-[#0176D3] focus:border-[#0176D3] h-16"
              />
              <p className="text-[11px] text-[#706E6B] mt-1.5">Rendered in small muted text below the submit button. Leave blank to hide.</p>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-[11px] font-semibold text-[#706E6B]">Custom Fields</label>
                <button type="button" onClick={addCustomField} className="text-xs font-bold text-[#0176D3] hover:text-[#0176D3]">+ Add Field</button>
              </div>
              <div className="space-y-3">
                {(config.customFields || []).length === 0 && (
                  <p className="text-xs text-[#706E6B] italic">No custom fields yet. Add one to collect information beyond the standard fields above.</p>
                )}
                {(config.customFields || []).map((field, idx) => (
                  <div key={field.key} className="border border-[#E5E5E5] rounded-lg p-3 space-y-2 bg-[#F3F3F3]/50">
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={field.label}
                        onChange={e => updateCustomField(idx, { label: e.target.value })}
                        placeholder="Field label"
                        className="flex-1 px-3 py-1.5 bg-white border border-[#D8DDE6] rounded text-xs font-medium focus:outline-none focus:ring-1 focus:ring-[#0176D3] focus:border-[#0176D3]"
                      />
                      <select
                        value={field.type}
                        onChange={e => updateCustomField(idx, { type: e.target.value })}
                        className="px-2 py-1.5 bg-white border border-[#D8DDE6] rounded text-xs"
                      >
                        <option value="text">Text</option>
                        <option value="textarea">Long Text</option>
                        <option value="email">Email</option>
                        <option value="tel">Phone</option>
                        <option value="number">Number</option>
                        <option value="date">Date</option>
                        <option value="dropdown">Dropdown</option>
                        <option value="radio">Radio Buttons</option>
                        <option value="checkbox">Checkbox</option>
                      </select>
                      <button type="button" onClick={() => removeCustomField(idx)} className="text-[#BA0517] hover:text-[#8E030F] text-[13px] font-semibold px-2 shrink-0">Remove</button>
                    </div>
                    {(field.type === 'dropdown' || field.type === 'radio') && (
                      <input
                        type="text"
                        value={(field.options || []).join(', ')}
                        onChange={e => updateCustomField(idx, { options: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
                        placeholder="Options, comma-separated (e.g. Small, Medium, Large)"
                        className="w-full px-3 py-1.5 bg-white border border-[#D8DDE6] rounded text-xs focus:outline-none focus:ring-1 focus:ring-[#0176D3] focus:border-[#0176D3]"
                      />
                    )}
                    <label className="flex items-center gap-2 text-xs text-[#5C5C5C]">
                      <input
                        type="checkbox"
                        checked={!!field.required}
                        onChange={e => updateCustomField(idx, { required: e.target.checked })}
                        className="rounded border-[#C9C9C9] text-[#0176D3] focus:ring-[#0176D3]"
                      />
                      Required
                      <span className="text-[#706E6B] ml-auto font-mono">key: {field.key}</span>
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="pt-4 border-t border-[#E5E5E5] flex justify-end gap-3">
            <button
              onClick={saveSettings}
              disabled={saving}
              className="flex items-center justify-center gap-2 bg-[#0176D3] hover:bg-[#014486] disabled:opacity-50 text-white h-9 px-4 rounded text-[13px] font-semibold transition cursor-pointer"
            >
              {saving ? <RefreshCw size={16} className="animate-spin" /> : <Save size={16} />}
              Save Form Settings
            </button>
          </div>
        </div>

        {/* Right Side: Preview & Code Panel */}
        <div className="lg:col-span-5 space-y-6">
          {/* Live Preview Card */}
          <div className="bg-white rounded-lg border border-[#E5E5E5] shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-6 space-y-6">
            <h2 className="font-bold text-[#2E2E2E] border-b border-[#E5E5E5] pb-3 flex items-center gap-1.5 text-sm">
              <Sparkles className="text-[#DD7A01]" size={16} /> Live Visual Preview
            </h2>

            <div className="border border-[#E5E5E5] rounded-lg p-6 bg-[#F3F3F3] space-y-4 overflow-x-auto">
              <div
                className="bg-white p-6 rounded-lg shadow-sm space-y-4 mx-auto"
                style={{ maxWidth: { 1: '24rem', 2: '32rem', 3: '40rem', 4: '48rem' }[config.columns || 1] }}
              >
                <div className="text-center space-y-2">
                  {config.logoUrl && (
                    <img src={config.logoUrl} alt="" className="h-10 mx-auto object-contain" onError={e => { e.currentTarget.style.display = 'none'; }} />
                  )}
                  <h3 className="font-bold text-lg text-[#2E2E2E]">{config.title || 'Contact Us'}</h3>
                  <p className="text-xs text-[#5C5C5C]">{config.description}</p>
                </div>

                <div
                  className={(config.columns || 1) > 1 ? `grid gap-3` : 'space-y-3'}
                  style={(config.columns || 1) > 1 ? { gridTemplateColumns: `repeat(${config.columns}, 1fr)` } : undefined}
                >
                  {config.fields.includes('name') && (
                    <div>
                      <label className="block text-xs font-semibold text-[#5C5C5C] mb-1">Full Name</label>
                      <input type="text" placeholder="John Doe" disabled className="w-full px-3 py-1.5 bg-white border border-[#D8DDE6] rounded text-xs" />
                    </div>
                  )}

                  {config.fields.includes('email') && (
                    <div>
                      <label className="block text-xs font-semibold text-[#5C5C5C] mb-1">Email Address</label>
                      <input type="email" placeholder="john@example.com" disabled className="w-full px-3 py-1.5 bg-white border border-[#D8DDE6] rounded text-xs" />
                    </div>
                  )}

                  {config.fields.includes('phoneNumber') && (
                    <div>
                      <label className="block text-xs font-semibold text-[#5C5C5C] mb-1">Phone Number</label>
                      <input type="text" placeholder="+1234567890" disabled className="w-full px-3 py-1.5 bg-white border border-[#D8DDE6] rounded text-xs" />
                    </div>
                  )}

                  {config.fields.includes('notes') && (
                    <div className={(config.columns || 1) > 1 ? 'col-span-full' : ''}>
                      <label className="block text-xs font-semibold text-[#5C5C5C] mb-1">Message / Notes</label>
                      <textarea placeholder="Tell us about your requirements…" disabled className="w-full px-3 py-1.5 bg-white border border-[#D8DDE6] rounded text-xs h-16" />
                    </div>
                  )}

                  {(config.customFields || []).map(field => {
                    const spansFull = (config.columns || 1) > 1 && ['checkbox', 'radio', 'textarea'].includes(field.type);
                    return (
                    <div key={field.key} className={spansFull ? 'col-span-full' : ''}>
                      {field.type === 'checkbox' ? (
                        <label className="flex items-center gap-2 text-xs font-medium text-[#5C5C5C]">
                          <input type="checkbox" disabled />
                          {field.label}
                        </label>
                      ) : field.type === 'radio' ? (
                        <>
                          <label className="block text-xs font-semibold text-[#5C5C5C] mb-1">{field.label}</label>
                          <div className="flex flex-wrap gap-x-4 gap-y-1">
                            {(field.options || []).map(o => (
                              <label key={o} className="flex items-center gap-1.5 text-xs font-medium text-[#5C5C5C]">
                                <input type="radio" name={`preview_${field.key}`} disabled />
                                {o}
                              </label>
                            ))}
                          </div>
                        </>
                      ) : (
                        <>
                          <label className="block text-xs font-semibold text-[#5C5C5C] mb-1">{field.label}</label>
                          {field.type === 'dropdown' ? (
                            <select disabled className="w-full px-3 py-1.5 bg-white border border-[#D8DDE6] rounded text-xs">
                              <option>Select…</option>
                              {(field.options || []).map(o => <option key={o}>{o}</option>)}
                            </select>
                          ) : field.type === 'textarea' ? (
                            <textarea placeholder="Sample text" disabled className="w-full px-3 py-1.5 bg-white border border-[#D8DDE6] rounded text-xs h-16" />
                          ) : (
                            <input
                              type={field.type === 'date' ? 'date' : (field.type || 'text')}
                              placeholder={field.type === 'date' ? '' : 'Sample text'}
                              disabled
                              className="w-full px-3 py-1.5 bg-white border border-[#D8DDE6] rounded text-xs"
                            />
                          )}
                        </>
                      )}
                    </div>
                    );
                  })}

                  <button
                    disabled
                    style={{ backgroundColor: config.themeColor }}
                    className={`text-white text-xs font-bold py-2 rounded shadow transition opacity-90 mt-2 ${(config.columns || 1) > 1 ? 'col-span-full' : 'w-full'}`}
                  >
                    Submit Request
                  </button>
                </div>

                {config.footerText && (
                  <p className="text-center text-[10px] text-[#706E6B] pt-3 border-t border-[#E5E5E5] whitespace-pre-line">{config.footerText}</p>
                )}
              </div>
            </div>
          </div>

          {/* Embed Code Card */}
          <div className="bg-[#F3F3F3] text-[#2E2E2E] border border-[#E5E5E5] rounded-lg p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-bold flex items-center gap-1.5 text-sm">
                <Code className="text-[#0176D3]" size={16} /> Embed Code (iframe)
              </h2>
              <button
                onClick={copyEmbedCode}
                className="p-1.5 bg-white border border-[#C9C9C9] hover:bg-[#F3F3F3] rounded text-[#0176D3] transition"
              >
                {copied ? <Check size={16} className="text-[#2E844A]" /> : <Copy size={16} />}
              </button>
            </div>
            <pre className="bg-white border border-[#E5E5E5] p-4 rounded text-[#2E2E2E] font-mono text-[10px] whitespace-pre-wrap break-all leading-normal">
              {iframeCode}
            </pre>
            <p className="text-[11px] text-[#5C5C5C] leading-normal">
              Copy and paste this code anywhere inside your website's body content to show the customized capture form.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
